import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowRight, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");
  
  const [status, setStatus] = useState(token ? "loading" : (emailParam ? "pending" : "resend"));
  const [message, setMessage] = useState(token ? "" : (emailParam ? "Please check your email for the verification link." : "Enter your email to resend verification."));
  const [email, setEmail] = useState(emailParam || "");
  const [resendEmail, setResendEmail] = useState(emailParam || "");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    
    let isMounted = true;
    
    const performVerification = async () => {
      // Try patient verification first, then clinic admin verification
      const endpoints = [
        `${BACKEND_URL}/api/auth/verify-email?token=${token}`,
        `${BACKEND_URL}/api/public/clinic-registration/verify-email?token=${token}`
      ];
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint);
          
          if (!isMounted) return;
          
          if (response.data.already_verified) {
            setStatus("already_verified");
            setMessage(response.data.message || "Your email is already verified.");
          } else {
            setStatus("success");
            setMessage(response.data.message || "Email verified successfully!");
          }
          setEmail(response.data.email || "");
          return; // Success - exit the loop
        } catch (error) {
          // Continue to next endpoint
          continue;
        }
      }
      
      // If we get here, all endpoints failed
      if (!isMounted) return;
      setStatus("error");
      setMessage("Failed to verify email. The link may have expired or is invalid.");
    };
    
    performVerification();
    
    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setResending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email: resendEmail });
      toast.success("Verification email sent! Please check your inbox.");
      setStatus("pending");
      setMessage("A new verification link has been sent to your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <Logo className="h-12 w-12" />
            <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              MyMedMember
            </span>
          </Link>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {status === "loading" && (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 text-teal-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Verifying Your Email
              </h2>
              <p className="text-gray-600">
                Please wait while we verify your email address...
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Email Verified! 🎉
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              {email && (
                <p className="text-sm text-gray-500 mb-6">
                  Verified email: <span className="font-medium">{email}</span>
                </p>
              )}
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
              >
                Continue to Login
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {status === "already_verified" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Already Verified
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <button
                onClick={handleGoToLogin}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
              >
                Go to Login
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {status === "pending" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-12 h-12 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Check Your Email
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-6 text-left">
                <h4 className="text-sm font-medium text-amber-800 mb-2">Did not receive the email?</h4>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• Check your spam or junk folder</li>
                  <li>• Make sure you entered the correct email</li>
                  <li>• Wait a few minutes and try again</li>
                </ul>
              </div>
              
              <form onSubmit={handleResendVerification} className="space-y-3">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
                <button
                  type="submit"
                  disabled={resending}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {status === "resend" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-12 h-12 text-gray-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Resend Verification
              </h2>
              <p className="text-gray-600 mb-6">
                Enter your email to receive a new verification link.
              </p>
              
              <form onSubmit={handleResendVerification} className="space-y-3">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
                <button
                  type="submit"
                  disabled={resending}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Verification Email
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {status === "error" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              
              <form onSubmit={handleResendVerification} className="space-y-3 mb-4">
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
                <button
                  type="submit"
                  disabled={resending}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Resend Verification
                    </>
                  )}
                </button>
              </form>
              
              <div className="space-y-3">
                <button
                  onClick={handleGoToLogin}
                  className="w-full py-3 px-6 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                >
                  Go to Login
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Links */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-gray-600">
            Already verified?{" "}
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              Sign in here
            </Link>
          </p>
          <p className="text-sm text-gray-600">
            Need an account?{" "}
            <Link to="/register" className="text-teal-600 hover:text-teal-700 font-medium">
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
